import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import moment from 'moment'
import { updateDaySetting } from '../api'
import { fetchData } from '../../../../../../global/utils/fetch'
import {
  IDaySettingPartial,
  IDaySetting,
} from '../../../../../../../../app/entities/DaySetting'
import { ITechnician } from '../../../../../../../../app/entities/Technician'
import { getActiveSchedulePattern } from '../../../../../../global/utils/schedule-patterns'
import ActivityLogModal from './ActivityLogModal'

interface UpdateIndividualDaySettingResult {
  success: boolean
  message: string
  requiresConfirmation?: boolean
  bumpedOrderIds?: number[]
  ordersAffected?: number
}

interface DaySettingsEditorProps {
  isOpen: boolean
  onClose: () => void
  selectedDate: Date | null
  technician: ITechnician
  currentDaySetting?: IDaySetting | null
  isWorkingDay: boolean
  isHumanTouched: boolean
  isHoliday?: boolean
  holidayName?: string
  onSaveChanges: (
    technicianId: string,
    daySettingData: IDaySettingPartial,
  ) => void
}

export default function DaySettingsEditor({
  isOpen,
  onClose,
  selectedDate,
  technician,
  currentDaySetting,
  isWorkingDay,
  isHumanTouched,
  isHoliday = false,
  holidayName,
  onSaveChanges,
}: DaySettingsEditorProps) {
  const [daySettingData, setDaySettingData] = useState<IDaySettingPartial>({})
  const [originalData, setOriginalData] = useState<IDaySettingPartial>({})
  const [confirmationData, setConfirmationData] = useState<{
    message: string
    bumpedOrderIds: number[]
    ordersAffected: number
  } | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showActivityLog, setShowActivityLog] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{
    startTime?: string
    endTime?: string
    lunchDuration?: string
  }>({})

  // Initialize editing state when modal opens
  useEffect(() => {
    if (isOpen && technician && selectedDate) {
      // Create proper UTC date at midnight for the calendar day
      const utcDate = new Date(
        Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          0,
          0,
          0,
          0, // midnight UTC
        ),
      )

      // Get active schedule pattern (most recently created)
      const { schedulePatterns = [] } = technician
      const activeSchedulePattern = getActiveSchedulePattern(schedulePatterns)

      // Priority for initial values: current day setting → schedule pattern → hardcoded defaults
      const defaultStartTime =
        currentDaySetting?.startTime ||
        activeSchedulePattern?.workDayFrom ||
        '08:00'

      const defaultEndTime =
        currentDaySetting?.endTime ||
        activeSchedulePattern?.workDayTo ||
        '17:00'

      const defaultLunchDuration =
        currentDaySetting?.lunchDuration ||
        activeSchedulePattern?.lunchDuration ||
        60

      const current: IDaySettingPartial = {
        day: utcDate,
        workDay: isWorkingDay,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
        lunchDuration: defaultLunchDuration,
        reason: '',
      }

      setDaySettingData(current)
      setOriginalData({ ...current })
      setIsSaving(false) // Reset loading state when modal opens
      setConfirmationData(null) // Clear any previous confirmation data
      setValidationErrors({}) // Clear any validation errors
    }
  }, [isOpen, technician, selectedDate, currentDaySetting, isWorkingDay])

  if (!selectedDate) return null

  const dateStr = moment(selectedDate).format('YYYY-MM-DD')
  const dayName = moment(selectedDate).format('dddd')
  const formattedDate = moment(selectedDate).format('MMMM Do, YYYY')

  const handleToggleWorking = (isWorking: boolean) => {
    // Get active schedule pattern (most recently created)
    const { schedulePatterns = [] } = technician
    const activeSchedulePattern = getActiveSchedulePattern(schedulePatterns)

    // Priority for defaults: current day setting → schedule pattern → hardcoded defaults
    const defaultStartTime =
      currentDaySetting?.startTime ||
      activeSchedulePattern?.workDayFrom ||
      '08:00'

    const defaultEndTime =
      currentDaySetting?.endTime || activeSchedulePattern?.workDayTo || '17:00'

    const defaultLunchDuration =
      currentDaySetting?.lunchDuration ||
      activeSchedulePattern?.lunchDuration ||
      60

    setDaySettingData({
      ...daySettingData,
      workDay: isWorking,
      startTime: isWorking ? defaultStartTime : undefined,
      endTime: isWorking ? defaultEndTime : undefined,
      lunchDuration: isWorking ? defaultLunchDuration : undefined,
      reason: daySettingData.reason || '',
    })
  }

  // Validation functions
  const validateTimes = (data: IDaySettingPartial) => {
    const errors: typeof validationErrors = {}

    if (data.workDay && data.startTime && data.endTime) {
      // Convert times to minutes for comparison
      const [startHours, startMinutes] = data.startTime.split(':').map(Number)
      const [endHours, endMinutes] = data.endTime.split(':').map(Number)
      const startTimeMinutes = startHours * 60 + startMinutes
      const endTimeMinutes = endHours * 60 + endMinutes

      if (startTimeMinutes >= endTimeMinutes) {
        errors.startTime = 'Start time must be before end time'
        errors.endTime = 'End time must be after start time'
      }

      // Check lunch duration vs work duration
      if (data.lunchDuration !== undefined && data.lunchDuration > 0) {
        const workDurationMinutes = endTimeMinutes - startTimeMinutes
        if (data.lunchDuration >= workDurationMinutes) {
          errors.lunchDuration = 'Lunch cannot be longer than work hours'
        }
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleTimeChange = (
    field: keyof IDaySettingPartial,
    value: string | number,
  ) => {
    const updatedData = {
      ...daySettingData,
      [field]: value,
    }
    setDaySettingData(updatedData)

    // Validate after state update
    setTimeout(() => validateTimes(updatedData), 0)
  }

  const handleSave = async (force: boolean = false) => {
    // Validate that changes have a reason
    if (!daySettingData.reason?.trim()) {
      alert('All changes require a reason')
      return
    }

    // Check for validation errors
    const isValid = validateTimes(daySettingData)
    if (!isValid) {
      alert('Please fix the validation errors before saving')
      return
    }

    setIsSaving(true)

    try {
      const apiCall = updateDaySetting(
        technician.objectId!,
        daySettingData,
        force,
      )
      const response = (await fetchData(
        apiCall,
      )) as UpdateIndividualDaySettingResult

      // Check if confirmation is required
      if (response.requiresConfirmation && !force) {
        setIsSaving(false) // Re-enable button so user can cancel
        setConfirmationData({
          message: response.message,
          bumpedOrderIds: response.bumpedOrderIds || [],
          ordersAffected: response.ordersAffected || 0,
        })
        return // Show confirmation modal, don't close
      }

      // Success - close modal and refresh
      setIsSaving(false)
      onSaveChanges(technician.objectId!, daySettingData)
      onClose()
    } catch (error) {
      console.error('Error updating day settings:', error)
      setIsSaving(false)
      alert('Failed to save day settings. Please try again.')
    }
  }

  const handleForceConfirm = async () => {
    setConfirmationData(null) // Close confirmation modal
    await handleSave(true) // Retry with force=true
  }

  const handleConfirmCancel = () => {
    setConfirmationData(null) // Just close confirmation modal
    setIsSaving(false) // Reset saving state
  }

  // Check if there are actual changes from original state
  const hasChanges =
    (daySettingData.workDay !== originalData.workDay ||
      daySettingData.startTime !== originalData.startTime ||
      daySettingData.endTime !== originalData.endTime ||
      daySettingData.lunchDuration !== originalData.lunchDuration) &&
    daySettingData.reason?.trim()

  // Check if there are any validation errors
  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='xs'
      >
        <DialogTitle
          style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <h3 style={{ margin: 0 }}>
                {dayName}, {formattedDate}
              </h3>
            </div>
            <IconButton
              onClick={onClose}
              size='small'
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          <div style={{ padding: '12px 0' }}>
            {isHoliday && (
              <div
                style={{
                  backgroundColor: '#fff3e0',
                  border: '2px solid #ff9800',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <h4 style={{ margin: '0 0 4px 0', color: '#e65100' }}>
                  {holidayName || 'Holiday'}
                </h4>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  This day is a company holiday and cannot be edited.
                </p>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: '16px' }}>
                  {technician.name}
                </h4>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  Currently: {isWorkingDay ? 'working' : 'off'}{' '}
                  {isHumanTouched ? '(manually set)' : ''}
                </span>
              </div>

              <FormControlLabel
                control={
                  <Switch
                    checked={daySettingData.workDay || false}
                    onChange={(e) => handleToggleWorking(e.target.checked)}
                    color='primary'
                    disabled={isHoliday}
                  />
                }
                label={daySettingData.workDay ? 'Working' : 'Off'}
              />
            </div>

            {/* Show time inputs if working */}
            {daySettingData.workDay && (
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}
                >
                  <div style={{ flex: 1 }}>
                    <TextField
                      label='Start Time'
                      type='time'
                      value={daySettingData.startTime || '08:00'}
                      onChange={(e) =>
                        handleTimeChange('startTime', e.target.value)
                      }
                      size='small'
                      InputLabelProps={{ shrink: true }}
                      error={!!validationErrors.startTime}
                      fullWidth
                      disabled={isHoliday}
                    />
                    {validationErrors.startTime && (
                      <div
                        style={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {validationErrors.startTime}
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <TextField
                      label='End Time'
                      type='time'
                      value={daySettingData.endTime || '17:00'}
                      onChange={(e) =>
                        handleTimeChange('endTime', e.target.value)
                      }
                      size='small'
                      InputLabelProps={{ shrink: true }}
                      error={!!validationErrors.endTime}
                      fullWidth
                      disabled={isHoliday}
                    />
                    {validationErrors.endTime && (
                      <div
                        style={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {validationErrors.endTime}
                      </div>
                    )}
                  </div>

                  <div style={{ width: '120px' }}>
                    <TextField
                      label='Lunch (min)'
                      type='number'
                      value={
                        daySettingData.lunchDuration !== undefined
                          ? daySettingData.lunchDuration
                          : 60
                      }
                      onChange={(e) => {
                        const value =
                          e.target.value === ''
                            ? 0
                            : parseInt(e.target.value) || 0
                        handleTimeChange('lunchDuration', value)
                      }}
                      size='small'
                      inputProps={{ min: 0, max: 480 }}
                      error={!!validationErrors.lunchDuration}
                      fullWidth
                      disabled={isHoliday}
                    />
                    {validationErrors.lunchDuration && (
                      <div
                        style={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {validationErrors.lunchDuration}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Reason input - hidden for holidays */}
            {!isHoliday && (
              <TextField
                label='Reason for change'
                value={daySettingData.reason || ''}
                onChange={(e) => handleTimeChange('reason', e.target.value)}
                fullWidth
                size='small'
                placeholder='Why is this day changing?'
                style={{ marginTop: '8px' }}
              />
            )}
          </div>
        </DialogContent>

        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Button
            onClick={() => setShowActivityLog(true)}
            size='small'
            color='inherit'
            style={{ marginRight: 'auto' }}
          >
            View Activity Log
          </Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={onClose}
              size='small'
            >
              {isHoliday ? 'Close' : 'Cancel'}
            </Button>
            {!isHoliday && (
              <Button
                onClick={() => handleSave()}
                variant='contained'
                size='small'
                disabled={!hasChanges || isSaving || hasValidationErrors}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </div>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal for Bumped Orders */}
      {confirmationData && (
        <Dialog
          open={!!confirmationData}
          onClose={handleConfirmCancel}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Confirm Schedule Change</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '16px' }}>
              <strong>{confirmationData.message}</strong>
            </div>

            {confirmationData.bumpedOrderIds.length > 0 && (
              <div>
                <p>
                  <strong>
                    Orders that will be bumped to pending rescheduling:
                  </strong>
                </p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {confirmationData.bumpedOrderIds.map((orderId) => (
                    <li key={orderId}>Work Order #{orderId}</li>
                  ))}
                </ul>
                <p
                  style={{
                    color: '#d70000',
                    fontSize: '14px',
                    fontStyle: 'italic',
                  }}
                >
                  Customer service will be notified to reschedule these orders.
                </p>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmCancel}>Cancel</Button>
            <Button
              onClick={handleForceConfirm}
              variant='contained'
              color='warning'
              style={{ backgroundColor: '#d70000' }}
            >
              Confirm & Bump Orders
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Activity Log Modal */}
      <ActivityLogModal
        open={showActivityLog}
        onClose={() => setShowActivityLog(false)}
        activityLog={currentDaySetting?.activityLog}
        technicianName={technician.name}
        selectedDate={selectedDate}
      />
    </>
  )
}
